import { useMemo, useCallback, useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import classnames from "classnames";

import { racingTypesList } from "../../constants/racingTypesList";
import { BetTypeEnum, BetTypeViewEnum } from "../../constants/enums/betType";
import { getSelectOptionsFromViewEnum } from "../../common/helpers";
import { toastWrapper } from "../../common/components/toastWrapper";
import { useTranslation } from "../../hooks/translation";
import { useConfig } from "../../hooks/config";
import mainStore from "../../stores/main";
import selectionsStore from "src/stores/selections";
import { betTypeFitsConfig, findProperBetType } from "src/common/helpers/betType";
import { getEffectiveSelectionsCount } from "src/common/helpers/betBuilder";

import "./style.scss";

const Header = () => {
  const t = useTranslation();
  const config = useConfig();
  const [mainState, setMainState] = useRecoilState(mainStore);
  const { selections } = useRecoilValue(selectionsStore);

  // Bet builder cards collapse to 1 each in the badge count.
  const badgeCount = getEffectiveSelectionsCount(selections);

  const isBankerEnabled = config.enableBankerBet

  const betTypes = useMemo(() => {
    const excludePattern = (key: number, value: string) =>
      !Object.keys(config.availableBetTypes).includes(value);
    return getSelectOptionsFromViewEnum(BetTypeViewEnum, t, excludePattern);
  }, [t, config]);

  const changeBetType = useCallback(
    (betType: BetTypeEnum) => {
      const betTypeData = config.availableBetTypes?.[BetTypeViewEnum[betType]];
      const isRacingType = racingTypesList.includes(+betType);

      if (isRacingType) setMainState({ ...mainState, betType });

      if (!betTypeData) return null;

      const effectiveCount = getEffectiveSelectionsCount(selections);
      const minSelectionsCountError = effectiveCount < (betTypeData.minSelectionsCount || Number.MIN_SAFE_INTEGER)
      const maxSelectionsCountError = effectiveCount > (betTypeData.maxSelectionsCount || Number.MAX_SAFE_INTEGER)

      const canChangeBetType = !minSelectionsCountError && !maxSelectionsCountError

      if (canChangeBetType) setMainState({ ...mainState, betType });
      else {
        const customErrorMessage = minSelectionsCountError ? betTypeData.minErrorMessage : betTypeData.maxErrorMessage
        toastWrapper("error", customErrorMessage || betTypeData.errorMessage);
      }
    },
    [config, mainState, setMainState, selections]
  );

  useEffect(() => {
    if (!betTypeFitsConfig(config, selections, mainState.betType)) {
      const properBetType = findProperBetType(config, selections);

      properBetType && properBetType !== mainState.betType && changeBetType(properBetType);
    }
  }, [config, selections, mainState, changeBetType]);

  return (
    <div className="B-betslip-header">
      {racingTypesList.includes(mainState.betType) ? (
        <div key={mainState.betType} className="B-active B-racing">
          <p className="B-header-txt">{BetTypeViewEnum[mainState.betType]}</p>
        </div>
      ) : (
        betTypes.map(betType => (
          <div
            key={betType.value}
            className={classnames({
              "B-active": mainState.betType === betType.value,
            })}
            onClick={() => changeBetType(betType.value)}
          >
            <p className="B-header-txt">{betType.name}</p>
            {
              !!isBankerEnabled && config.enableBankerNewLabel && betType.value === BetTypeEnum.System && <span className="B-banker-new">{t('New')}</span>
            }
            {!!selections.length && mainState.betType === betType.value && (
              <span className="B-badge">{badgeCount}</span>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default Header;
